/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useContext, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";

import { BiPlusCircle, BiSearch, BiX, BiChevronRight } from "react-icons/bi";
import { routePaths } from "../../baseLayout/SidebarMenu";
import { Loading, useRedirect } from "react-admin";
import { APIContext } from "../../store/APIStore";
import IAccount from "../../dataProvider/interfaces/IAccount";
import { GenericList } from "../../components";
import { theme } from "../../theme";
import { dataType } from "../../components/GenericList/GenericList";
import { BiTrash } from "react-icons/bi";
import { useLocation } from "react-router-dom";
import AccountForm from "./AccountForm/AccountForm";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import { useHasPermissions } from "../../hooks/useHasPermissions";
import { PERMISSIONS } from "../../roles";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import FdButton from "../../components/UIComponents/Button/FdButton";
import { accountOverview as classes} from "./styles"


const AccountsOverview = () => {
  const [searchKey, setSearchKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<IAccount[]>([]);
  const [accountsCount, setAccountsCount] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState<IAccount>();
  const api = useContext(APIContext);
  const redirect = useRedirect();
  const location = useLocation().search;
  const notificationRef = useRef<any>();

  // permissions
  const canManageAccount = useHasPermissions(PERMISSIONS.CAN_MANAGE_ACCOUNT);

  // redirect if no permission to access this page
  useEffect(() => {
    if (canManageAccount === false) {
      redirect(`/${routePaths.ACTIVE_EVENTS}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageAccount]);

  // load data
  useEffect(() => {
    // read url params
    const search = new URLSearchParams(location).get("search");

    if (search) {
      setSearchKey(search);
      fetchAccounts(search);
    } else {
      fetchAccounts("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAccounts = async (keyword: string, start?: number) => {
    if (!start) {
      setLoading(true);
    }

    try {
      const { data, count } = (await api.accountService.getAccounts(
        keyword,
        start
      )) as any;
      setAccountsCount(count);
      if (start) {
        setAccounts([...accounts, ...data]);
      } else {
        setAccounts(data);
        setSelectedAccount(data[0]);
      }

      setLoading(false);
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const onSearchClick = (e: any) => {
    redirect(`/${routePaths.ACCOUNTS}?search=${searchKey}`);
    fetchAccounts(searchKey);
  };

  const handleSearchClear = () => {
    redirect(`/${routePaths.ACCOUNTS}`);
    setSearchKey("");
    fetchAccounts("");
  };

  const handleSearchKeyDown = (e: any) => {
    if (e.keyCode === 13) {
      redirect(`/${routePaths.ACCOUNTS}?search=${searchKey}`);
      fetchAccounts(searchKey);
    }
  };

  const handleBulkDelete = async (items: number[]) => {
    try {
      await Promise.all(
        items.map((item) => {
          return api.accountService.deleteAccount(item);
        })
      );
      fetchAccounts("");
      notificationRef.current?.show(
        "Accounts successfully deleted",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleDeleteAccount = async (id: number) => {
    try {
      await api.accountService.deleteAccount(id);
      fetchAccounts("");
      notificationRef.current?.show(
        "Account successfully deleted",
        notificationType.Success
      );
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  const handleListPagination = () => {
    if (accounts.length < accountsCount) {
      fetchAccounts(searchKey, accounts.length);
    }
  };

  const handleAccountSave = (newAccount: IAccount) => {
    let newAccountsList;
    if (
      accounts.filter((account: IAccount) => account.id === newAccount.id)[0]
    ) {
      newAccountsList = accounts.map((account: IAccount) => {
        if (account.id === newAccount.id) {
          return { ...account, ...newAccount };
        } else {
          return account;
        }
      });
      setAccounts(newAccountsList);
      setSelectedAccount(newAccount);
    } else {
      // newUserList = [...userList, ...[newUser]];
      fetchAccounts("");
    }

    notificationRef.current?.show(
      "Account successfully saved",
      notificationType.Success
    );
  };

  // actions applied on selected items
  const bulkActions = [
    {
      label: "Delete",
      key: "delete",
      callback: handleBulkDelete,
      startIcon: <BiTrash />,
    },
  ];

  return (
    <>
      <Box sx={classes.wrapper}>
        {/* top actions   */}
        <Box sx={classes.topActions}>
          <Box flexGrow="1">
            <FdTypography label="Accounts" component="h3" variant="h3" />
          </Box>
          <FdButton
            size="medium"
            startIcon={<BiPlusCircle />}
            onClick={(event) => {
              setSelectedAccount(undefined);
            }}
            ButtonName="Create Account"
          />
        </Box>

        {/* search bar */}
        <Box sx={classes.topFilters} display="flex">
          {/* <Paper sx={classes.searchForm}> */}
            <FdTextField
              sx={classes.searchInput}
              placeholder="Search"
              value={searchKey}
              onChange={(e) => {
                const keyword = e.target.value;
                setSearchKey(keyword);
              } }
              autoFocus
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BiSearch size="18" />
                  </InputAdornment>
                ),
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={classes.clearIcon}
                  onClick={handleSearchClear}
                >
                  {searchKey.length > 0 && <BiX size="20" />}
                </InputAdornment>
              ),
            }} />
          {/* </Paper> */}
          <FdButton
            onClick={onSearchClick}
            sx={classes.searchButton}
            ButtonName="Search"
          />
        </Box>

        {/* page content */}
        {!loading && (
          <Box sx={classes.listContent}>
            <GenericList
              items={accounts}
              onSelectItem={(item: IAccount) => {
                setSelectedAccount(item);
              }}
              type={dataType.ACCOUNTS}
              onBottomScroll={handleListPagination}
              onDeleteItem={handleDeleteAccount}
              selectedItem={selectedAccount}
              bulkActions={bulkActions}
            />
            <BiChevronRight size="50" color={theme.palette.grey["800"]} />
            <AccountForm
              account={selectedAccount}
              onAccountSave={handleAccountSave}
              onError={(error: string) => {
                notificationRef.current?.show(error, notificationType.Error);
              }}
            />
          </Box>
        )}

        {loading && <Loading />}
      </Box>

      <NotificationMessage ref={notificationRef} />
    </>
  );
};

export default AccountsOverview;
